import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { Icon } from "@iconify-icon/solid";
import { LoadingButton } from "../../components/ui/Button";
import { SelectInput } from "../../components/ui/form/SelectInput";
import { TextInput } from "../../components/ui/form/TextInput";
import useCheckupTest from "../../services/checkup/useCheckupTest";
import { onMount, For, Show, createEffect, createSignal } from "solid-js";
import { Button } from "@/components/shadcn/button";


export default function CheckupTest(props) {

  const { tests, testForm, onSaveTest, handleTestInput, handleTestResult, onFetchTests, addTest, removeTest, setTestForm } = useCheckupTest(props);
  const [expandedTests, setExpandedTests] = createSignal(new Set());
  const [isInitialized, setIsInitialized] = createSignal(false);

  onMount(() => {
    onFetchTests();
    // Register save function with parent component
    if (props.onRegisterSave) {
      props.onRegisterSave(onSaveTest);
    }
    // Set initialized flag after a brief delay to ensure props are stable
    setTimeout(() => setIsInitialized(true), 100);
  });

  const toggleTestExpansion = (testKey) => {
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testKey)) {
        newSet.delete(testKey);
      } else {
        newSet.add(testKey);
      }
      return newSet;
    });
  };


  const onFormChange = () => {
    const originalTests = props.tests || [];
    const currentTests = testForm.tests || [];

    // Compare tests to see if there are changes
    const hasChanges = originalTests.length !== currentTests.length;
    if (hasChanges) {
      props.onFormChange(true);
      return;
    }

    // Check for any changes in test results by comparing with originalResults
    let hasTestChanges = false;
    currentTests.forEach((currentTest) => {
      // Parse originalResults if it's a JSON string
      let originalResults = {};
      if (currentTest.originalResults) {
        try {
          originalResults = typeof currentTest.originalResults === 'string' 
            ? JSON.parse(currentTest.originalResults) 
            : currentTest.originalResults;
        } catch (e) {
          originalResults = {};
        }
      }
      
      const currentResults = currentTest.results || {};
      
      // Get all unique keys from both objects
      const allKeys = new Set([...Object.keys(originalResults), ...Object.keys(currentResults)]);
      
      // Check if any value is different
      for (const key of allKeys) {
        const originalValue = originalResults[key] || '';
        const currentValue = currentResults[key] || '';
        
        if (originalValue !== currentValue) {
          hasTestChanges = true;
          break;
        }
      }
      
      if (hasTestChanges) return; // Exit early if changes found
    });

    props.onFormChange(hasTestChanges);
  };

  // Track form changes
  createEffect(() => {
    if (props.onFormChange && isInitialized()) {
      onFormChange();
    }
  });

  const content = (
    <>
      <Show when={!props.hideCard}>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Icon icon="mdi:stethoscope" class="text-gray-500 me-2" />
            Tests
          </CardTitle>
        </CardHeader>
      </Show>
      <div class={props.hideCard ? "space-y-4" : ""}>
        {/* Add New Test */}
        <div class="pt-0">
          <h4 class="font-medium text-gray-900 mb-3 dark:text-gray-100">
            Tests
          </h4>
          <div class="flex gap-2 mb-4">
            <div class="flex-1">
              <SelectInput
                label="Select Test"
                name="testId"
                form={testForm}
                options={tests()}
                input={handleTestInput}
                placeholder="Choose a test..."
              />
            </div>
            <div class="flex items-end">
              <Button variant="outline" onClick={addTest}>
                Add Test
              </Button>
            </div>
          </div>
          
          {/* All Tests List */}
          <Show
            when={
              testForm.tests &&
              testForm.tests.length > 0
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <For each={testForm.tests}>
                {(test) => (
                  <div class="bg-gray-50 rounded-lg border dark:bg-gray-800 dark:border-gray-700">
                    {/* Test Header */}
                    <div class="flex items-center justify-between px-3 py-2">
                      <div class="flex-1">
                        <p class="font-medium text-sm">{test.name}</p>
                      </div>
                      <div class="flex items-center gap-2">
                        {/* Delete button */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeTest(test.key)}
                          class="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 w-8 h-8"
                        >
                          <Icon icon="material-symbols-light:delete-outline-rounded" class="w-4 h-4" />
                        </Button>
                        
                        {/* Collapsible button - only show if test has options */}
                        <Show when={test.options && test.options.length > 0}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => toggleTestExpansion(test.key)}
                            class="p-1 w-8 h-8"
                          >
                            <Icon 
                              icon={expandedTests().has(test.key) ? "mdi:chevron-up" : "mdi:chevron-down"} 
                              class="w-4 h-4" 
                            />
                          </Button>
                        </Show>
                      </div>
                    </div>
                    
                    {/* Test Options - Collapsible */}
                    <Show when={test.options && test.options.length > 0 && expandedTests().has(test.key)}>
                      <div class="px-3 pb-2 border-t border-gray-200 dark:border-gray-600">
                        <div class="pt-2 space-y-2">
                          <For each={test.options}>
                            {(option, index) => (
                              <div class="flex items-center gap-2">
                                <span class="text-sm text-gray-600 dark:text-gray-400 flex-1">
                                  {option.trim()}:
                                </span>
                                <div class="flex items-center gap-1">
                                  <input
                                    name={`option-${test.key}-${index()}`}
                                    class="w-24 h-8 me-1 text-xs px-2 border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-800"
                                    placeholder={'0'}
                                    type={'number'}
                                    value={test.results?.[option.trim()] || ""}
                                    onInput={(ev) => {
                                      handleTestResult(test.key, option.trim(), ev.target.value);
                                    }}
                                  />
                                  <span class="text-xs text-gray-500">year(s)</span>
                                </div>
                              </div>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <Show
            when={
              !testForm.tests ||
              testForm.tests.length === 0
            }
          >
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-4">
              No tests assigned yet
            </p>
          </Show>

          <Show when={!props.hideSaveButton}>
            <LoadingButton
              loading={props.loading}
              onClick={onSaveTest}
              disabled={!testForm.tests}
              class="mt-4"
            >
              Save Tests
            </LoadingButton>
          </Show>
        </div>
      </div>
    </>
  );

  return (
    <Show when={props.hideCard} fallback={<Card><CardContent class="space-y-4">{content}</CardContent></Card>}>
      {content}
    </Show>
  );
}
